/**
 * @description Shared status pills, including invoice overdue / due-today.
 * @dependencies invoiceHelpers.isInvoiceOverdue, isInvoiceDueToday; CompanyContext
 * @notes Invoice overdue uses the company primary-location IANA timezone (same
 * clock as backend companyLocalTodaySql), not the viewer's laptop timezone.
 */
import React, { useContext } from 'react';
import {
  isInvoiceOverdue,
  isInvoiceDueToday
} from '../../utils/invoiceHelpers';
import CompanyContext from '../../contexts/CompanyContext';

const StatusBadge = ({ type, value, size = 'sm' }) => {
  const companyTimeZone = useContext(CompanyContext)?.company?.timezone;
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-xs',
    sm: 'px-2.5 py-1 text-sm',
    md: 'px-3 py-1.5 text-base'
  };

  const getStyleForType = () => {
    switch (type) {
      case 'payment_status':
        switch (value?.toLowerCase()) {
          case 'paid':
            return 'bg-green-100 text-green-800 border-green-200';
          case 'unpaid':
            return 'bg-red-100 text-red-800 border-red-200';
          case 'partial':
            return 'bg-yellow-100 text-yellow-800 border-yellow-200';
          default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
        }
      
      case 'entry_mode':
        switch (value?.toLowerCase()) {
          case 'chip':
            return 'bg-blue-100 text-blue-800 border-blue-200';
          case 'contactless':
            return 'bg-purple-100 text-purple-800 border-purple-200';
          case 'ecommerce':
            return 'bg-green-100 text-green-800 border-green-200';
          case 'keyed':
            return 'bg-gray-100 text-gray-800 border-gray-200';
          case 'swiped':
            return 'bg-indigo-100 text-indigo-800 border-indigo-200';
          default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
        }
      
        case 'channel':
          switch (value?.toLowerCase()) {
            case 'merchant_portal':
              return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'customer_portal':
              return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'api':
              return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'invoice':
              return 'bg-green-100 text-green-800 border-green-200';
            case 'recurring':
              return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'external':
              return 'bg-gray-100 text-gray-800 border-gray-200';
            default:
              return 'bg-gray-100 text-gray-800 border-gray-200';
          }
      
      case 'transaction_type':
        switch (value?.toLowerCase()) {
          case 'purchase':
            return 'bg-gray-100 text-gray-800 border-gray-200';
          case 'refund':
            return 'bg-orange-100 text-orange-800 border-orange-200';
          case 'void':
            return 'bg-red-100 text-red-800 border-red-200';
          default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
        }

      case 'transaction_status':
        const { status, paymentStatus, isVoided, settlementStatus, transactionType } = value || {};
        if (isVoided) return 'bg-gray-100 text-gray-800 border-gray-200';
        if (status === 'refunded') return 'bg-orange-100 text-orange-800 border-orange-200';
        if (status === 'partially_refunded') return 'bg-orange-100 text-orange-800 border-orange-200';
        if (transactionType === 'refund') return 'bg-green-100 text-green-800 border-green-200';
        if (status === 'DECLINED') return 'bg-red-100 text-red-800 border-red-200';
        if (status === 'APPROVAL') return 'bg-green-100 text-green-800 border-green-200';
        if (paymentStatus === 'paid' && settlementStatus === 'settled') return 'bg-green-100 text-green-800 border-green-200';
        if (paymentStatus === 'paid') return 'bg-blue-100 text-blue-800 border-blue-200';
        if (status === 'PENDING') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        return 'bg-gray-100 text-gray-600 border-gray-200';

      case 'invoice_status':
        const { status: invStatus, paymentStatus: invPaymentStatus, dueDate } = value || {};
        const invoiceTimeZone = value?.timeZone || companyTimeZone;
        const overdue = typeof value?.isOverdue === 'boolean'
          ? value.isOverdue
          : Boolean(
            dueDate
            && isInvoiceOverdue(dueDate, invPaymentStatus, invoiceTimeZone)
            && (invPaymentStatus === 'unpaid' || invPaymentStatus === 'partially_paid')
          );

        // Priority order matches original InvoiceDetails.js and InvoiceList.js logic
        if (invPaymentStatus === 'paid') return 'bg-green-100 text-green-800 border-green-200';
        if (invPaymentStatus === 'partially_paid' && !overdue) return 'bg-orange-100 text-orange-800 border-orange-200';
        if (invPaymentStatus === 'voided') return 'bg-gray-500 text-white border-gray-500';
        if (invStatus === 'cancelled') return 'bg-gray-100 text-gray-800 border-gray-200';
        
        if (overdue && (invPaymentStatus === 'unpaid' || invPaymentStatus === 'partially_paid')) {
          return 'bg-red-50 text-red-600 border border-red-200';
        }
        if (dueDate && isInvoiceDueToday(dueDate, invoiceTimeZone)) {
          return 'bg-gray-100 text-gray-700 border border-gray-300';
        }
        
        // Invoice document status
        if (invStatus === 'open') return 'bg-blue-100 text-blue-800 border-blue-200';
        if (invStatus === 'void') return 'bg-gray-100 text-gray-600 border-gray-200';
        return 'bg-gray-100 text-gray-800 border-gray-200';

      case 'estimate_status':
        switch (value?.toLowerCase()) {
          case 'draft': return 'bg-gray-100 text-gray-700 border-gray-200';
          case 'sent': return 'bg-blue-100 text-blue-700 border-blue-200';
          case 'accepted': return 'bg-green-100 text-green-700 border-green-200';
          case 'declined': return 'bg-red-100 text-red-700 border-red-200';
          case 'expired': return 'bg-orange-100 text-orange-700 border-orange-200';
          case 'converted': return 'bg-purple-100 text-purple-700 border-purple-200';
          default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }

      case 'recurring_status':
        switch (value?.toLowerCase()) {
          case 'active': return 'bg-green-100 text-green-700 border-green-200';
          case 'paused': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
          case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
          case 'completed': return 'bg-purple-100 text-purple-700 border-purple-200';
          default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }

      case 'avs_code':
        switch (value) {
          case 'Y':
          case 'D':
          case 'M': return 'bg-green-100 text-green-800 border-green-200';
          case 'A':
          case 'Z':
          case 'B':
          case 'P': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
          case 'N':
          case 'E': return 'bg-red-100 text-red-800 border-red-200';
          case 'U':
          case 'S': return 'bg-gray-100 text-gray-800 border-gray-200';
          default: return 'bg-gray-100 text-gray-600 border-gray-200';
        }

      case 'cvv_code':
        switch (value) {
          case 'M': return 'bg-green-100 text-green-800 border-green-200';
          case 'N':
          case 'I': return 'bg-red-100 text-red-800 border-red-200';
          case 'P':
          case 'S':
          case 'U': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
          default: return 'bg-gray-100 text-gray-600 border-gray-200';
        }

      case 'card_present':
        return value ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-800 border-gray-200';

      case 'vendor_status':
        return value ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200';

      case 'user_status':
        return value ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200';

      case 'compliance_status':
        return value ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200';
      
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDisplayValue = () => {
    if (!value) return 'N/A';
    
    // Format the value for display
    switch (type) {
      case 'entry_mode':
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      case 'channel':
        return value.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
      case 'payment_status':
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      case 'transaction_type':
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      
      case 'transaction_status':
        const { status, paymentStatus, isVoided, settlementStatus, transactionType } = value || {};
        if (isVoided) return 'Voided';
        if (status === 'refunded') return 'Refunded';
        if (status === 'partially_refunded') return 'Partially Refunded';
        if (transactionType === 'refund') return 'Complete';
        if (status === 'DECLINED') return 'Declined';
        if (status === 'APPROVAL') return 'Approved';
        if (paymentStatus === 'paid' && settlementStatus === 'settled') return 'Completed';
        if (paymentStatus === 'paid') return 'Authorized';
        if (status === 'PENDING') return 'Processing';
        return 'Unknown';

      case 'invoice_status':
        const { status: invStatus, paymentStatus: invPaymentStatus, dueDate } = value || {};
        const invoiceTimeZone = value?.timeZone || companyTimeZone;
        const overdue = typeof value?.isOverdue === 'boolean'
          ? value.isOverdue
          : Boolean(
            dueDate
            && isInvoiceOverdue(dueDate, invPaymentStatus, invoiceTimeZone)
            && (invPaymentStatus === 'unpaid' || invPaymentStatus === 'partially_paid')
          );

        // Priority order matches original InvoiceDetails.js and InvoiceList.js logic
        if (invPaymentStatus === 'paid') return 'Paid';
        if (invPaymentStatus === 'partially_paid' && !overdue) return 'Partially Paid';
        if (invPaymentStatus === 'voided') return 'Voided';
        if (invStatus === 'void') return 'Void';

        if (overdue && (invPaymentStatus === 'unpaid' || invPaymentStatus === 'partially_paid')) {
          return 'Overdue';
        }
        if (dueDate && isInvoiceDueToday(dueDate, invoiceTimeZone)) {
          return 'Due Today';
        }

        // Invoice document status
        if (invStatus === 'open') return 'Open';
        return 'Open';

      case 'estimate_status':
        const estimateStatusMap = {
          'draft': 'Draft', 'sent': 'Sent', 'accepted': 'Accepted',
          'declined': 'Declined', 'expired': 'Expired', 'converted': 'Converted'
        };
        return estimateStatusMap[value?.toLowerCase()] || value || 'Unknown';

      case 'recurring_status':
        const recurringStatusMap = {
          'active': 'Active', 'paused': 'Paused',
          'cancelled': 'Cancelled', 'completed': 'Completed'
        };
        return recurringStatusMap[value?.toLowerCase()] || value || 'Unknown';

      case 'avs_code':
        switch (value) {
          case 'Y': return 'Address Match';
          case 'D': return 'Exact Match';
          case 'M': return 'Postal Match';
          case 'A': return 'Address Only';
          case 'Z': return 'Postal Only';
          case 'B': return 'Street Match';
          case 'P': return 'Postal Match';
          case 'N': return 'No Match';
          case 'E': return 'Error';
          case 'U': return 'Unavailable';
          case 'S': return 'Not Supported';
          default: return value || 'Unknown';
        }

      case 'cvv_code':
        switch (value) {
          case 'M': return 'CVV Match';
          case 'N': return 'CVV No Match';
          case 'I': return 'CVV Invalid';
          case 'P': return 'Not Processed';
          case 'S': return 'Not Supported';
          case 'U': return 'Unavailable';
          default: return value || 'Unknown';
        }

      case 'card_present':
        return value ? 'Card Present' : 'Card Not Present';

      case 'vendor_status':
        return value ? 'Active' : 'Inactive';

      case 'user_status':
        return value ? 'Active' : 'Inactive';

      case 'compliance_status':
        return value ? 'Compliant' : 'Non-Compliant';
      
      default:
        return value;
    }
  };

  return (
    <span className={`inline-flex items-center font-medium rounded-full border ${sizeClasses[size]} ${getStyleForType()}`}>
      {getDisplayValue()}
    </span>
  );
};

export default StatusBadge;